<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{
      fixed: true,
      absolute: isTablet,
      order: 1,
      overlay: (isTablet || isMobile) && !isSidebarMinimized,
    }"
    @leftOverlayClick="onSidebarOverlayClick"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar
        :visible="isMobile ? !isSidebarMinimized : true"
        :minimized="isSidebarMinimized"
        :animated="!isMobile"
        :mobile="isMobile"
        @update:visible="onSidebarVisibilityUpdate"
        @toggleMinimize="isSidebarMinimized = !isSidebarMinimized"
      />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article>
      </main>
    </template>
  </VaLayout>

  <!-- Session Warning Modal -->
  <SessionWarningModal />
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'
import { useSessionTracker } from '../composables/useSessionTracker'
import { useSwipeGesture } from '../composables/useSwipeGesture'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import SessionWarningModal from '../components/session/SessionWarningModal.vue'

// Initialize session tracking for authenticated users
useSessionTracker()

// Initialize swipe gesture for mobile sidebar control
useSwipeGesture({
  threshold: 100,
  onSwipe: (direction) => {
    if (isMobile.value) {
      if (direction.right && isSidebarMinimized.value) {
        // Swipe right to open sidebar
        isSidebarMinimized.value = false
      } else if (direction.left && !isSidebarMinimized.value) {
        // Swipe left to close sidebar
        isSidebarMinimized.value = true
      }
    }
  },
})

const GlobalStore = useGlobalStore()
const route = useRoute()

const { current } = useBreakpoint()
const breakpoints = computed(() => {
  const bp = current?.value || {}
  return bp
})

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)
const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  // Use consistent mobile detection
  const isMobileByWidth = window.innerWidth <= 640 // sm breakpoint
  const isTabletByWidth = window.innerWidth <= 1024 // md breakpoint

  // Use breakpoints if available, otherwise fall back to window dimensions
  const wasMobile = isMobile.value
  isMobile.value = breakpoints.value?.smDown ?? isMobileByWidth
  isTablet.value = breakpoints.value?.mdDown ?? isTabletByWidth

  // Only force minimization on transition to mobile
  if (isMobile.value && !wasMobile) {
    isSidebarMinimized.value = true
  }

  // Update sidebar dimensions
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)

  // Listen for clicks on navigation links to handle same-route navigation
  const handleNavigationClick = (event) => {
    // Check if the click is on a navigation link or within navigation area
    const target = event.target.closest(
      '.sidebar-content a, .sidebar-content .va-sidebar-link, .sidebar-content [role="button"]',
    )

    // Exempt user menu items from this behavior
    const isUserMenuClick = event.target.closest('.user-menu-item')

    if (target && !isUserMenuClick) {
      // Check if we're on mobile/tablet and minimize sidebar
      if ((isMobile.value || isTablet.value) && !isSidebarMinimized.value) {
        isSidebarMinimized.value = true
      }
    }
  }

  // Add click listener to the entire document to catch navigation clicks
  document.addEventListener('click', handleNavigationClick, true) // Use capture phase

  // Keyboard navigation for sidebar
  const handleKeydown = (event) => {
    // ESC key to close sidebar on mobile
    if (event.key === 'Escape' && isMobile.value && !isSidebarMinimized.value) {
      isSidebarMinimized.value = true
      event.preventDefault()
    }
  }

  window.addEventListener('keydown', handleKeydown)

  // Initialize mobile/tablet state immediately using window dimensions
  const initializeMobileState = () => {
    const isMobileByWidth = window.innerWidth <= 640 // sm breakpoint
    const isTabletByWidth = window.innerWidth <= 1024 // md breakpoint

    // Set initial mobile/tablet state
    isMobile.value = isMobileByWidth
    isTablet.value = isTabletByWidth

    // Force sidebar to be minimized on mobile devices
    if (isMobile.value) {
      isSidebarMinimized.value = true
    }

    // Set sidebar dimensions
    sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
    sidebarWidth.value = isTablet.value ? '100%' : '16rem'
  }

  // Initialize immediately
  initializeMobileState()

  // Then sync with breakpoints when they become available
  const syncWithBreakpoints = () => {
    if (current?.value) {
      // Only update if breakpoints give different results
      const bpMobile = breakpoints.value?.smDown
      const bpTablet = breakpoints.value?.mdDown

      if (bpMobile !== undefined && bpMobile !== isMobile.value) {
        isMobile.value = bpMobile
        if (isMobile.value) {
          isSidebarMinimized.value = true
        }
      }

      if (bpTablet !== undefined && bpTablet !== isTablet.value) {
        isTablet.value = bpTablet
      }

      // Update dimensions after sync
      sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
      sidebarWidth.value = isTablet.value ? '100%' : '16rem'
    } else {
      // Retry if breakpoints not ready
      setTimeout(syncWithBreakpoints, 50)
    }
  }

  // Sync with breakpoints after a short delay to avoid conflicts
  setTimeout(syncWithBreakpoints, 100)

  // Store the cleanup function for keydown and click listeners
  return () => {
    window.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('click', handleNavigationClick, true)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  // Always collapse sidebar after route change on mobile and tablet
  if (isMobile.value || isTablet.value) {
    isSidebarMinimized.value = true
  }
})

// Watch for route changes, including navigation to the same route
watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    // Only minimize if we're on mobile/tablet and the path actually changed
    if (newPath !== oldPath && (isMobile.value || isTablet.value)) {
      isSidebarMinimized.value = true
    }
  },
  { immediate: false, flush: 'post' },
)

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}

const onSidebarOverlayClick = () => {
  isSidebarMinimized.value = true
}

const onSidebarVisibilityUpdate = (visible) => {
  if (!visible) {
    isSidebarMinimized.value = true
  }
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

// Improved mobile responsiveness
.app-layout__sidebar-wrapper {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.minimized {
    padding-left: 0;
  }

  @media (max-width: 768px) {
    padding-left: 0 !important;
  }
}

// Ensure content doesn't overlap on mobile
@media (max-width: 768px) {
  :deep(.va-layout__area--content) {
    margin-left: 0 !important;
    width: 100% !important;
  }

  :deep(.va-layout__area--left) {
    position: fixed !important;
    z-index: 1000;
  }
}

// Smooth transitions for sidebar toggle
:deep(.va-layout) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

// Improve overlay appearance
:deep(.va-layout__overlay) {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  transition: all 0.3s ease;
}

// Better spacing for mobile
@media (max-width: 640px) {
  main {
    padding: 1rem !important;

    &.pt-0 {
      padding-top: 0 !important;
    }
  }

  .p-4 {
    padding: 1rem !important;

    &.pt-0 {
      padding-top: 0 !important;
    }
  }
}
</style>
